// Dedicated Auth Store - Phase 3: State Management Optimization
import { create } from 'zustand';
import { persist, subscribeWithSelector, devtools } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
  error?: string;
}

interface AuthActions {
  setAuth: (user: User | null, session: Session | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error?: string) => void;
  clearError: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateEmail: (newEmail: string) => Promise<void>;
  initialize: () => Promise<void>;
}

type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          // Initial state
          user: null,
          session: null,
          loading: true,
          initialized: false,
          error: undefined,

          // Actions
          setAuth: (user, session) => {
            set({ user, session, error: undefined });
          },

          setLoading: (loading) => {
            set({ loading });
          },

          setError: (error) => {
            set({ error });
          },

          clearError: () => {
            set({ error: undefined });
          },

          signIn: async (email: string, password: string) => {
            try {
              set({ loading: true, error: undefined });
              const { data, error } = await supabase.auth.signInWithPassword({ email, password });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign in failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          signUp: async (email: string, password: string) => {
            try {
              set({ loading: true, error: undefined });
              const { error } = await supabase.auth.signUp({ 
                email, 
                password,
                options: {
                  emailRedirectTo: `${window.location.origin}/`
                }
              });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign up failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          signOut: async () => {
            try {
              set({ loading: true, error: undefined });
              const { error } = await supabase.auth.signOut();
              if (error) throw error;
              
              set({ 
                user: null, 
                session: null
              });
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Sign out failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          resetPassword: async (email: string) => {
            try {
              set({ loading: true, error: undefined });
              const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/auth/reset-password`
              });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          updatePassword: async (newPassword: string) => {
            try {
              set({ loading: true, error: undefined });
              const { error } = await supabase.auth.updateUser({ password: newPassword });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Password update failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          updateEmail: async (newEmail: string) => {
            try {
              set({ loading: true, error: undefined });
              const { error } = await supabase.auth.updateUser({ email: newEmail });
              if (error) throw error;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : 'Email update failed';
              set({ error: errorMessage });
              throw error;
            } finally {
              set({ loading: false });
            }
          },

          initialize: async () => {
            try {
              if (get().initialized) return;

              set({ loading: true, error: undefined });

              const { data: { session }, error } = await supabase.auth.getSession();
              if (error) {
                console.error('Error getting initial session:', error);
              }
              
              set({ 
                session, 
                user: session?.user ?? null, 
                loading: false,
                initialized: true 
              });

              // Set up auth state listener
              supabase.auth.onAuthStateChange((event, session) => {
                try {
                  set({ 
                    session, 
                    user: session?.user ?? null,
                    loading: false,
                    error: undefined
                  });
                  
                  if (event === 'SIGNED_IN') {
                    window.location.href = '/dashboard';
                  } else if (event === 'SIGNED_OUT') {
                    window.location.href = '/auth';
                  }
                } catch (error) {
                  console.error('Error in auth state change listener:', error);
                  set({ error: 'Authentication state change failed' });
                }
              });
            } catch (error) {
              console.error('Critical error during auth initialization:', error);
              const errorMessage = error instanceof Error ? error.message : 'Auth initialization failed';
              
              set({ 
                session: null, 
                user: null, 
                loading: false,
                initialized: true,
                error: errorMessage
              });
              throw error;
            }
          },
        }),
        {
          name: 'auth-storage',
          partialize: (state) => ({
            user: state.user,
            session: state.session,
          }),
        }
      )
    ),
    { name: 'Auth Store' }
  )
);

// Selectors for optimized re-renders
export const useUser = () => useAuthStore((state) => state.user);
export const useSession = () => useAuthStore((state) => state.session);
export const useAuthLoading = () => useAuthStore((state) => state.loading);
export const useAuthError = () => useAuthStore((state) => state.error);
export const useIsAuthenticated = () => useAuthStore((state) => !!state.user);